import { NavLink, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import {
  usePermissions,
  clearPermissionCache,
} from "@/contexts/PermissionContext";
import { token, currentUser, BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { useCurrentUser } from "@/hooks/useCurrentUser";

type NavEntry =
  | {
      kind: "link";
      to: string;
      label: string;
      icon: ReactNode;
      permission?: string;
    }
  | { kind: "divider"; label: string };

const superAdminNavItems: NavEntry[] = [
  {
    kind: "link",
    to: "/dashboard",
    label: "Dashboard",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/partners",
    label: "Partners",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M15 8h1M15 12h1M15 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/partner-reports",
    label: "Partner Reports",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18M7 16l4-4 4 4 4-5" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/service-providers",
    label: "Service Providers",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/platform-users",
    label: "Platform Users",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/audit-logs",
    label: "Audit Logs",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 12h6M9 16h6M9 8h2M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
        <circle cx="9" cy="8" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/roles",
    label: "Roles & Permissions",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2z" />
        <path d="M12 14c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z" />
        <path d="M17 9h4M19 7v4" />
      </svg>
    ),
  },
  { kind: "divider", label: "Support" },
  {
    kind: "link",
    to: "/iprs-wallet",
    label: "IPRS Report",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20M7 15h2M15 15h2" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/stanbic-report",
    label: "Stanbic Report",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
    ),
  },
];

const navItems: NavEntry[] = [
  {
    kind: "link",
    to: "/dashboard",
    label: "Dashboard",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/customers",
    label: "Customers",
    permission: "customers.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/businesses",
    label: "Businesses",
    permission: "customers.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 21h18M5 21V7l7-4 7 4v14M9 11h1M14 11h1M9 15h1M14 15h1" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/batch-checks",
    label: "Batch Checks",
    permission: "checks.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/mpesa-statements",
    label: "M-Pesa Statements",
    permission: "statements.mpesa.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/bank-statements",
    label: "Bank Statements",
    permission: "statements.bank.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/telco-statements",
    label: "Airtel/MTN Statements",
    permission: "statements.telco.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M12 18h.01" />
        <path d="M9 6h6M9 10h6M9 14h4" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/merchant-transactions",
    label: "Merchant Transactions",
    permission: "statements.transactions.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.4 6h12.8M10 19.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0zm7 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/brs-checks",
    label: "Business Verification",
    permission: "checks.brs.run",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/usage-report",
    label: "Usage Report",
    permission: "reports.usage",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/billing",
    label: "Billing",
    permission: "billing.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <path d="M1 10h22" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/crb-products",
    label: "CRB Products",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    ),
  },

  { kind: "divider", label: "Admin" },
  {
    kind: "link",
    to: "/scoring-configs",
    label: "Scoring Configs",
    permission: "settings.scoring",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        <path d="M4 15h3M15 4l3 3" />
        <line x1="9" y1="5" x2="9" y2="5.01" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/topup",
    label: "Top-up Account",
    permission: "billing.topup",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v8M8 12h8" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/developer",
    label: "Developer",
    permission: "settings.developer",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/users",
    label: "Users",
    permission: "settings.users",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },

  { kind: "divider", label: "Finance" },
  {
    kind: "link",
    to: "/partner-reports",
    label: "Partner Reports",
    permission: "reports.partner.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18M7 16l4-4 4 4 4-5" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/service-providers",
    label: "Service Providers",
    permission: "providers.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/iprs-wallet",
    label: "IPRS Report",
    permission: "iprs.wallet.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20M7 15h2M15 15h2" />
      </svg>
    ),
  },

  { kind: "divider", label: "Support" },
  {
    kind: "link",
    to: "/request-stats",
    label: "Request Stats",
    permission: "logs.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18M7 16l4-4 4 4 4-5" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/request-logs",
    label: "Request Logs",
    permission: "logs.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 12h6M9 16h6M9 8h6M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/iprs-requests",
    label: "IPRS Requests",
    permission: "logs.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/stanbic-report",
    label: "Stanbic Report",
    permission: "logs.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/pbp-onboarding",
    label: "PBP Onboarding",
    permission: "pbp.onboard",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M19 8v6M22 11h-6" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/notifications",
    label: "Notifications",
    permission: "logs.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },

  { kind: "divider", label: "Super Admin" },
  {
    kind: "link",
    to: "/partners",
    label: "Partners",
    permission: "partners.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M15 8h1M15 12h1M15 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/partner-reports",
    label: "Partner Reports",
    permission: "reports.partner.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3v18h18M7 16l4-4 4 4 4-5" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/service-providers",
    label: "Service Providers",
    permission: "providers.view",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/platform-users",
    label: "Platform Users",
    permission: "platform.roles",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/audit-logs",
    label: "Audit Logs",
    permission: "platform.roles",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 12h6M9 16h6M9 8h2M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
        <circle cx="9" cy="8" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    kind: "link",
    to: "/roles",
    label: "Roles & Permissions",
    permission: "platform.roles",
    icon: (
      <svg
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2z" />
        <path d="M12 14c-5.33 0-8 2.67-8 4v1h16v-1c0-1.33-2.67-4-8-4z" />
        <path d="M17 9h4M19 7v4" />
      </svg>
    ),
  },
];

function pickSections(sections: string[]): NavEntry[] {
  const allowed = new Set(sections);
  const result: NavEntry[] = [];
  let section = "";
  for (const entry of navItems) {
    if (entry.kind === "divider") {
      section = entry.label;
      if (allowed.has(section)) result.push(entry);
    } else if (allowed.has(section)) {
      result.push(entry);
    }
  }
  return result;
}

const adminNavItems = pickSections(["", "Admin"]);
const superFinanceNavItems = pickSections(["", "Finance", "Support"]);

const ugandaHiddenRoutes = new Set([
  "/mpesa-statements",
  "/batch-checks",
  "/brs-checks",
  "/crb-products",
  "/iprs-wallet",
  "/iprs-requests",
]);

const supportNavItems: NavEntry[] = navItems.filter((entry) => {
  if (entry.kind === "divider") return entry.label === "Support";
  if (entry.kind === "link" && entry.to === "/dashboard") return true;
  const supportPerms = new Set(["logs.view", "logs.retrigger", "pbp.onboard"]);
  return !!entry.permission && supportPerms.has(entry.permission);
});

export function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { can, isSuperAdmin, roleKey, isPermissionsReady } = usePermissions();
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await fetch(`${BASE_URL}/api/v1/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } catch { /* best-effort */ }
    token.clear();
    currentUser.clear();
    clearPermissionCache();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  const isVisible = (entry: NavEntry) => {
    if (entry.kind === "divider") return true;
    if (user?.countryCode?.toUpperCase() === "UG" && ugandaHiddenRoutes.has(entry.to)) return false;
    if (!entry.permission) return true;
    return can(entry.permission);
  };

  const sourceItems = isSuperAdmin
    ? superAdminNavItems
    : roleKey === "admin"
      ? adminNavItems
      : roleKey === "super_finance"
        ? superFinanceNavItems
        : roleKey === "support"
          ? supportNavItems
          : navItems;

  const visibleItems = sourceItems.reduce<NavEntry[]>((acc, entry, i) => {
    if (entry.kind === "divider") {
      const sectionItems = sourceItems
        .slice(i + 1)
        .filter((e) => e.kind !== "divider");
      const nextDivider = sourceItems
        .slice(i + 1)
        .findIndex((e) => e.kind === "divider");
      const section =
        nextDivider === -1 ? sectionItems : sectionItems.slice(0, nextDivider);
      if (section.some((e) => isVisible(e))) acc.push(entry);
    } else {
      if (isVisible(entry)) acc.push(entry);
    }
    return acc;
  }, []);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-20 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center border-b border-slate-200 bg-gradient-to-r from-brand-50/60 via-white to-white px-5">
          <img src="/logo.png" alt="Patascore" className="h-7 w-auto" />
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 [scrollbar-width:thin] [scrollbar-color:transparent_transparent] hover:[scrollbar-color:theme(colors.slate.200)_transparent]">
          {!isPermissionsReady ? (
            <ul className="space-y-1">
              {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="px-3 py-2.5">
                  <div
                    className="h-4 animate-pulse rounded-md bg-slate-100"
                    style={{ width: `${60 + (i % 3) * 15}%` }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-0.5">
              {visibleItems.map((entry, i) =>
                entry.kind === "divider" ? (
                  <li key={i} className="pb-1 pt-5 first:pt-2">
                    <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      {entry.label}
                    </p>
                  </li>
                ) : (
                  <li key={entry.to}>
                    <NavLink
                      to={entry.to}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 before:absolute before:left-0 before:top-1/2 before:h-5 before:w-1 before:-translate-y-1/2 before:rounded-r-full before:content-[''] before:transition-opacity before:duration-150 ${
                          isActive
                            ? "bg-brand-50 text-brand-700 before:bg-gradient-to-b before:from-brand-400 before:to-brand-600 before:opacity-100"
                            : "text-slate-600 before:opacity-0 hover:bg-slate-100 hover:text-slate-900"
                        }`
                      }
                    >
                      {entry.icon}
                      <span className="truncate">{entry.label}</span>
                    </NavLink>
                  </li>
                ),
              )}
            </ul>
          )}
        </nav>

        {/* Logout */}
        <div className="border-t border-slate-200 p-3">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-all duration-150 hover:bg-red-50 hover:text-red-600"
          >
            <svg
              className="h-[18px] w-[18px] shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
